import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useApiResource } from "../api/useApi";
import { getSettings } from "../api/resources";
import { NEXT_FEATURES } from "../featureFlags";
import {
  ME,
  ORGS,
  MY_MEMBERSHIPS,
  GLOBAL_ORG_ID,
  PERSONAL_INTEGRATIONS,
  planLabel,
  ROLE_LABEL,
  SHARE_SCOPES,
  ShareScope,
} from "../mockData";
import { IntegrationGrid } from "../components/IntegrationGrid";
import { PageTabs } from "../components/PageTabs";
import {
  User,
  Shield,
  Sparkles,
  Bell,
  Building2,
  Globe2,
  Plus,
  ArrowRight,
  Crown,
  LogOut,
  KeyRound,
  Workflow,
  Check,
} from "lucide-react";

type Tab = "profile" | "privacy" | "ai" | "notifications" | "integrations" | "orgs" | "account";

const ALL_TABS = [
  { key: "profile",       label: "Profile",          icon: User },
  { key: "privacy",       label: "Privacy & sharing", icon: Shield },
  { key: "ai",            label: "AI preferences",   icon: Sparkles },
  { key: "notifications", label: "Notifications",    icon: Bell },
  { key: "integrations",  label: "Integrations",     icon: Workflow },
  { key: "orgs",          label: "My organisations", icon: Building2 },
  { key: "account",       label: "Account",          icon: KeyRound },
];

// v1 keeps just the identity tab. Account credentials (email, password,
// sessions) are managed by Auth0 directly, so the Account tab is redundant
// here. The rest of the tabs need real backend wiring or multi-org plumbing
// and stay behind NEXT_FEATURES.
const TABS = NEXT_FEATURES
  ? ALL_TABS
  : ALL_TABS.filter((t) => t.key === "profile");

export default function Profile() {
  const { setOrgId, currentOrgId } =
    useOutletContext<{ setOrgId: (id: string) => void; currentOrgId?: string }>() as any;
  const [tab, setTab] = useState<Tab>("profile");
  const { user } = useAuth0();

  // Pre-load server-side preferences. The form fields below are still bound
  // to mock state until the settings DTO is finalised.
  useApiResource((apiFetch) => getSettings(apiFetch), []);

  const displayName = user?.name ?? ME.name;
  const displayEmail = user?.email ?? "mia@eleetz.app";
  const displayInitials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ?? ME.initials;

  return (
    <>
      <PageTabs tabs={TABS} value={tab} onChange={(v) => setTab(v as Tab)} />

      <div className="space-y-4">
        {/* Compact identity strip — same shape as other page intros */}
        <div className="card p-4 flex items-center gap-4 flex-wrap">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-el-300 to-el-700 text-white text-base font-semibold flex items-center justify-center">
              {displayInitials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold text-slate-900">{displayName}</div>
              <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">{ME.primaryActivity}</span>
              {user?.email_verified && (
                <span className="badge bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                  <Check className="h-3 w-3" /> Verified
                </span>
              )}
            </div>
            <div className="text-[0.7rem] text-slate-500 mt-0.5">
              {displayEmail} · joined {ME.joinedAt} · {MY_MEMBERSHIPS.length} organisations
            </div>
          </div>
          <button className="btn-secondary !h-9">View public profile</button>
        </div>

        <div className="card p-5">
          {tab === "profile"       && <ProfileTab />}
          {tab === "privacy"       && <PrivacyTab />}
          {tab === "ai"            && <AITab />}
          {tab === "notifications" && <NotificationsTab />}
          {tab === "integrations"  && <IntegrationsTab />}
          {tab === "orgs"          && <OrgsTab currentOrgId={currentOrgId ?? GLOBAL_ORG_ID} setOrgId={setOrgId} />}
          {tab === "account"       && <AccountTab />}
        </div>
      </div>
    </>
  );
}

function Field({ label, value, hint, type = "text" }: { label: string; value: string; hint?: string; type?: string }) {
  return (
    <label className="block">
      <div className="label-eyebrow">{label}</div>
      <input
        type={type}
        defaultValue={value}
        className="mt-1 w-full h-9 px-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
      />
      {hint && <div className="text-[0.7rem] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

function Toggle({ label, hint, defaultOn = false }: { label: string; hint?: string; defaultOn?: boolean }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <button type="button" onClick={() => setOn((v) => !v)} className="w-full flex items-start justify-between gap-4 py-3 text-left">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <span className={["h-5 w-9 rounded-full relative transition shrink-0", on ? "bg-el-500" : "bg-slate-200"].join(" ")}>
        <span className={["absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition", on ? "left-4" : "left-0.5"].join(" ")} />
      </span>
    </button>
  );
}

function ScopePicker({ defaultScope }: { defaultScope: ShareScope }) {
  const [scope, setScope] = useState<ShareScope>(defaultScope);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {SHARE_SCOPES.map((s) => (
        <button
          key={s.id}
          onClick={() => setScope(s.id)}
          className={[
            "text-left rounded-md border p-3 transition",
            scope === s.id ? "border-el-300 bg-el-50/60 ring-1 ring-el-200" : "border-slate-200 hover:border-el-200",
          ].join(" ")}
        >
          <div className="text-sm font-semibold text-slate-900">{s.label}</div>
          <div className="text-[0.7rem] text-slate-500 mt-0.5">{s.hint}</div>
        </button>
      ))}
    </div>
  );
}

/* ---------- tabs ---------- */

function ProfileTab() {
  const { user } = useAuth0();
  if (!user) {
    return (
      <div className="text-sm text-slate-500">
        Log in to see your account details.
      </div>
    );
  }

  const provider = user.sub?.split("|")[0];
  const userId = user.sub?.split("|")[1];
  const lastUpdated = user.updated_at
    ? new Date(user.updated_at).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Avatar card */}
      <div className="rounded-md border border-slate-200 p-5 flex flex-col items-center text-center md:col-span-1">
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name ?? "Profile"}
            className="h-22 w-22 rounded-full object-cover shadow"
            style={{ height: 88, width: 88 }}
          />
        ) : (
          <div
            className="rounded-full bg-gradient-to-br from-el-300 to-el-700 text-white text-2xl font-semibold flex items-center justify-center shadow"
            style={{ height: 88, width: 88 }}
          >
            {(user.name ?? ME.name)
              .split(" ")
              .map((p) => p[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
        )}
        <div className="mt-3 text-base font-semibold text-slate-900">
          {user.name}
        </div>
        <div className="text-xs text-slate-500">{user.email}</div>
        {user.email_verified && (
          <span className="mt-3 badge bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
            <Check className="h-3 w-3" /> Verified
          </span>
        )}
      </div>

      {/* Account information */}
      <div className="rounded-md border border-slate-200 p-5 md:col-span-2">
        <div className="text-sm font-semibold text-slate-900 mb-4">
          Account information
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReadField label="Full name" value={user.name} />
          <ReadField label="Nickname" value={user.nickname} />
          <ReadField label="Email" value={user.email} />
          <ReadField label="Email verified" value={user.email_verified ? "Yes" : "No"} />
          <ReadField label="Auth provider" value={provider} />
          <ReadField label="User ID" value={userId} />
          {lastUpdated && <ReadField label="Last updated" value={lastUpdated} />}
          {user.locale && <ReadField label="Locale" value={user.locale} />}
        </div>
      </div>
    </div>
  );
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-sm text-slate-900 font-medium break-words">
        {value || <span className="text-slate-300">—</span>}
      </div>
    </div>
  );
}

function PrivacyTab() {
  return (
    <div className="space-y-4">
      <div>
        <div className="label-eyebrow mb-2">Default share scope for new captures</div>
        <ScopePicker defaultScope="friends" />
        <div className="text-[0.7rem] text-slate-500 mt-2">
          AI feedback always runs on your sessions — this controls who else can see the resulting
          report and clip.
        </div>
      </div>
      <div className="border-t border-slate-100 pt-2">
        <Toggle label="Auto-publish to Explore"   hint="Surfaces public sessions in the global feed" />
        <Toggle label="Allow friends to compare"  hint="Mates on your roster can run side-by-side comparisons" defaultOn />
        <Toggle label="Show me on leaderboards"   hint="Eleetz Global weekly + monthly boards" defaultOn />
      </div>
    </div>
  );
}

function AITab() {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-el-50/60 ring-1 ring-el-100 p-3 text-xs text-slate-700">
        AI runs on every session by default. These toggles control how loud it is, not whether it's on.
      </div>
      <div>
        <Toggle label="Generate AI summary on every session" hint="Off only stops the written summary; phase detection still runs" defaultOn />
        <Toggle label="Suggest drills tied to detected issues" defaultOn />
        <Toggle label="Compare to my last 4 sessions automatically" defaultOn />
        <Toggle label="Voice-over the report on the mobile app" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
        <Field label="Tone"   value="Direct" hint="Direct · Encouraging · Technical" />
        <Field label="Length" value="Concise" hint="Concise · Standard · Verbose" />
      </div>
    </div>
  );
}

function IntegrationsTab() {
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500 max-w-2xl">
        Personal connections live on your account and follow you across every organisation.
        Wearables, health, social, personal storage.
      </div>
      <IntegrationGrid items={PERSONAL_INTEGRATIONS} />
    </div>
  );
}

function NotificationsTab() {
  return (
    <div className="space-y-1">
      <Toggle label="Session processed"           hint="When a capture finishes processing" defaultOn />
      <Toggle label="New PR detected"             hint="Personal records on any metric" defaultOn />
      <Toggle label="Friend posts a public swing" defaultOn />
      <Toggle label="Coach leaves a note"         defaultOn />
      <Toggle label="Weekly progress digest" />
    </div>
  );
}

function OrgsTab({ currentOrgId, setOrgId }: { currentOrgId: string; setOrgId: (id: string) => void }) {
  const memberships = MY_MEMBERSHIPS
    .map((m) => ({ org: ORGS.find((o) => o.id === m.orgId)!, role: m.role }))
    .filter((x) => x.org);

  const onlyGlobal = memberships.length === 1 && memberships[0].org.id === GLOBAL_ORG_ID;

  return (
    <div className="space-y-4">
      <ul className="space-y-2.5">
        {memberships.map(({ org, role }) => {
          const isCurrent = org.id === currentOrgId;
          const isGlobal = org.id === GLOBAL_ORG_ID;
          return (
            <li key={org.id} className={["rounded-md border p-4 flex items-start gap-3", isCurrent ? "border-el-200 bg-el-50/40" : "border-slate-200"].join(" ")}>
              <div className={["h-10 w-10 rounded-md flex items-center justify-center shrink-0", isGlobal ? "bg-slate-100 text-slate-600 ring-1 ring-slate-200" : "bg-gradient-to-br from-el-400 to-el-700 text-white"].join(" ")}>
                {isGlobal ? <Globe2 className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-900">{org.name}</div>
                  <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">{planLabel(org.plan)}</span>
                  <span className="badge bg-white text-slate-500 ring-1 ring-slate-200">{ROLE_LABEL[role]}</span>
                  {isCurrent && <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">Current</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {org.type} · {org.athletes.toLocaleString()} athletes · {org.weeklySessions.toLocaleString()} sessions / week
                </div>
                {isGlobal && org.description && (
                  <div className="text-[0.7rem] text-slate-500 mt-1">{org.description}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!isCurrent && (
                  <button onClick={() => setOrgId(org.id)} className="btn-secondary !h-8 !text-xs">Switch</button>
                )}
                <button className="btn-ghost !h-8 !text-xs">Leave</button>
              </div>
            </li>
          );
        })}
      </ul>

      <button className="btn-secondary !h-9 !text-xs">
        <Plus className="h-3.5 w-3.5" /> Create organisation
      </button>

      {onlyGlobal && (
        <div className="rounded-md border border-el-200 bg-gradient-to-br from-el-50 to-white p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-el-100 text-el-700 flex items-center justify-center"><Crown className="h-4 w-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">Spin up your own organisation</div>
            <p className="text-xs text-slate-600 mt-0.5">
              Private roster, branded reports, billing and per-coach seats. Free trial for the first 14 days.
            </p>
          </div>
          <button className="btn-primary !h-9">Upgrade <ArrowRight className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

function AccountTab() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Password"        value="••••••••"   hint="Last changed 2026-04-12" type="password" />
        <Field label="Two-factor auth" value="Enabled · TOTP" />
      </div>
      <div className="border-t border-slate-100 pt-4">
        <Toggle label="Email me on new device sign-in" defaultOn />
        <Toggle label="Allow third-party app authorisation" defaultOn />
      </div>
      <div className="border-t border-slate-100 pt-4 flex items-center gap-3">
        <button className="btn-secondary !h-9">Download my data</button>
        <button className="btn-ghost !h-9 !text-rose-600">
          <LogOut className="h-4 w-4" /> Delete account
        </button>
      </div>
    </div>
  );
}
